"use client";

import { c as _c } from "react/compiler-runtime";
import { cjk } from "@streamdown/cjk";
import { createCodePlugin } from "@streamdown/code";
import { math } from "@streamdown/math";
import "katex/dist/katex.min.css";
import { type ComponentProps, memo } from "react";
import { Streamdown } from "streamdown";
import { lazyMermaid } from "@/components/ai-elements/lazy-mermaid";
import { cn } from "@/lib/utils";
import type { BundledTheme } from "shiki";
type ResponseProps = ComponentProps<typeof Streamdown> & {
  isStreaming?: boolean;
};
const shikiTheme: [BundledTheme, BundledTheme] = ["github-light", "github-dark"];
const code = createCodePlugin({
  themes: ["github-light", "github-dark"]
});
const TRUSTED_LINK_HOSTS = ["llmgateway.io"];

// Trusted links open directly; everything else goes through Streamdown's
// open/cancel confirmation gate.
const isTrustedLink = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return false;
    }
    if (parsed.origin === window.location.origin) {
      return true;
    }
    return TRUSTED_LINK_HOSTS.some(host => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`));
  } catch {
    return false;
  }
};
export const Response = memo(t0 => {
  const $ = _c(12);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      isStreaming: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const isStreaming = t1 === undefined ? false : t1;
  let t2;
  if ($[4] !== className) {
    t2 = cn("size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_ul]:pl-5 [&_ol]:pl-5", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      code,
      mermaid: lazyMermaid,
      math,
      cjk
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      enabled: true,
      onLinkCheck: isTrustedLink
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isStreaming || $[9] !== props || $[10] !== t2) {
    t5 = <Streamdown className={t2} isAnimating={isStreaming} plugins={t3} shikiTheme={shikiTheme} linkSafety={t4} {...props} />;
    $[8] = isStreaming;
    $[9] = props;
    $[10] = t2;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
});
Response.displayName = "Response";